import { useAuth } from '@/lib'
import { logger } from '@/ui'
import { authUpdateLanguage } from '@jobilla/nexus-sdk'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES, type SupportedLanguage } from '../locales'

export type UseLanguagePreferenceReturns = {
	currentLanguage: SupportedLanguage
	changeLanguage: (lang: SupportedLanguage) => Promise<void>
	isLoading: boolean
}

function isSupportedLanguage(value: unknown): value is SupportedLanguage {
	return typeof value === 'string' && (SUPPORTED_LANGUAGES as readonly string[]).includes(value)
}

export const LOCALE_STORAGE_KEY = 'preferred_locale'

/**
 * Picks the best initial locale from three sources, in priority order:
 *   1. The authoritative server-stored value (`user.language`).
 *   2. The previously-cached value in localStorage (used pre-auth or as fallback).
 *   3. The default language.
 */
function resolveInitialLocale(serverLanguage: string | null | undefined): SupportedLanguage {
	if (isSupportedLanguage(serverLanguage)) {
		return serverLanguage
	}
	const stored = localStorage.getItem(LOCALE_STORAGE_KEY)
	if (isSupportedLanguage(stored)) {
		return stored
	}
	return DEFAULT_LANGUAGE
}

export function useLanguagePreference(): UseLanguagePreferenceReturns {
	const { user, isLoading: isAuthLoading } = useAuth()
	const { i18n } = useTranslation()
	const [isLoading, setIsLoading] = useState(isAuthLoading)

	useEffect(() => {
		if (isAuthLoading) {
			return
		}

		const desired = resolveInitialLocale(user?.language ?? null)
		if (desired !== i18n.language) {
			void i18n.changeLanguage(desired)
		}
		if (localStorage.getItem(LOCALE_STORAGE_KEY) !== desired) {
			localStorage.setItem(LOCALE_STORAGE_KEY, desired)
		}

		setIsLoading(false)
	}, [isAuthLoading, user?.language, i18n])

	const changeLanguage = useCallback(async (lang: SupportedLanguage) => {
		const previousLanguage = i18n.language

		// Server sync only when authenticated. Pre-auth users (login / SSO flow)
		// can still switch language locally; swallow auth/network errors mid-session
		// so a transient 401 doesn't block the visible UI change.
		if (user) {
			try {
				await authUpdateLanguage({ language: lang }, { withCredentials: true })
			} catch (error) {
				logger.error(error, {
					category: 'language-preference',
					data: { attempted_language: lang, previous_language: previousLanguage },
				})
			}
		}

		try {
			await i18n.changeLanguage(lang)
			localStorage.setItem(LOCALE_STORAGE_KEY, lang)
		} catch (error) {
			// Unexpected local failure — roll back so the UI and the cached locale stay in sync.
			await i18n.changeLanguage(previousLanguage)
			throw error
		}

		logger.track('System language changed', {
			data: {
				previous_language: previousLanguage,
				new_language: lang,
			},
		})
	}, [i18n, user])

	const currentLanguage = isSupportedLanguage(i18n.language) ? i18n.language : DEFAULT_LANGUAGE

	return { currentLanguage, changeLanguage, isLoading }
}
